import { Injectable } from '@angular/core';

/**
 * VAPT "Clear Text Credentials" (ADR-036): builds the login envelope "v1.{K}.{IV}.{C}"
 * (unpadded base64url). A random AES-256-GCM key encrypts {username,password,timestamp,nonce}
 * (AAD "v1"); that key is wrapped with the server's RSA public key (RSA-OAEP, SHA-256).
 * Must match LoginPayloadDecryptor on the API.
 */
@Injectable({
  providedIn: 'root'
})
export class LoginCryptoService {

  isSupported(): boolean {
    return typeof crypto !== 'undefined' && !!crypto.subtle && typeof crypto.getRandomValues === 'function';
  }

  // crypto.randomUUID is newer than Web Crypto; fall back to a UUID v4 built from getRandomValues.
  private newNonce(): string {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }

  async encryptLogin(publicKeyBase64: string, username: string, password: string): Promise<string> {
    const subtle = crypto.subtle;
    const rsaKey = await subtle.importKey(
      'spki',
      this.fromBase64(publicKeyBase64),
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt']
    );

    const aesKey = await subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plaintext = new TextEncoder().encode(JSON.stringify({
      username,
      password,
      timestamp: new Date().toISOString(),
      nonce: this.newNonce()
    }));

    const cipherAndTag = await subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: new TextEncoder().encode('v1'), tagLength: 128 },
      aesKey,
      plaintext
    );
    const wrappedKey = await subtle.encrypt({ name: 'RSA-OAEP' }, rsaKey, await subtle.exportKey('raw', aesKey));

    return ['v1', this.toBase64Url(wrappedKey), this.toBase64Url(iv), this.toBase64Url(cipherAndTag)].join('.');
  }

  private fromBase64(b64: string): ArrayBuffer {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  }

  private toBase64Url(data: ArrayBuffer | Uint8Array): string {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
